/**
 * US Federal Holidays Library
 *
 * Provides standard US holidays for scheduling exceptions.
 * Holidays recur annually within the schedule's date range.
 */
/**
 * Standard US Federal Holidays (fixed dates)
 */
export const US_HOLIDAYS = [
    {
        id: 'new-years-day',
        name: "New Year's Day",
        date: '01-01',
        description: 'First day of the year',
        type: 'federal',
    },
    {
        id: 'independence-day',
        name: 'Independence Day',
        date: '07-04',
        description: 'US Independence Day',
        type: 'federal',
    },
    {
        id: 'veterans-day',
        name: 'Veterans Day',
        date: '11-11',
        description: 'Honor military veterans',
        type: 'federal',
    },
    {
        id: 'christmas',
        name: 'Christmas',
        date: '12-25',
        description: 'Christmas Day',
        type: 'federal',
    },
];
/**
 * Get nth weekday of month
 * @param year Year
 * @param month Month (1-12)
 * @param weekday Day of week (0=Sunday, 1=Monday, etc.)
 * @param occurrence Which occurrence (1-5, negative for last)
 */
function getNthWeekdayOfMonth(year, month, weekday, occurrence) {
    const firstDay = new Date(year, month - 1, 1);
    const firstWeekday = firstDay.getDay();
    let offset = weekday - firstWeekday;
    if (offset < 0)
        offset += 7;
    if (occurrence > 0) {
        const day = 1 + offset + (occurrence - 1) * 7;
        return new Date(year, month - 1, day);
    }
    else {
        // Last occurrence
        const lastDay = new Date(year, month, 0);
        const lastWeekday = lastDay.getDay();
        let daysBack = lastWeekday - weekday;
        if (daysBack < 0)
            daysBack += 7;
        return new Date(year, month - 1, lastDay.getDate() - daysBack);
    }
}
export const FLOATING_HOLIDAYS = [
    {
        id: 'mlk-day',
        name: 'Martin Luther King Jr. Day',
        calculator: (year) => {
            // 3rd Monday in January
            const date = getNthWeekdayOfMonth(year, 1, 1, 3);
            return date.toISOString().split('T')[0];
        },
        description: 'Honor Martin Luther King Jr.',
        type: 'federal',
    },
    {
        id: 'presidents-day',
        name: "Presidents' Day",
        calculator: (year) => {
            // 3rd Monday in February
            const date = getNthWeekdayOfMonth(year, 2, 1, 3);
            return date.toISOString().split('T')[0];
        },
        description: "Honor US Presidents",
        type: 'federal',
    },
    {
        id: 'memorial-day',
        name: 'Memorial Day',
        calculator: (year) => {
            // Last Monday in May
            const date = getNthWeekdayOfMonth(year, 5, 1, -1);
            return date.toISOString().split('T')[0];
        },
        description: 'Honor fallen soldiers',
        type: 'federal',
    },
    {
        id: 'labor-day',
        name: 'Labor Day',
        calculator: (year) => {
            // 1st Monday in September
            const date = getNthWeekdayOfMonth(year, 9, 1, 1);
            return date.toISOString().split('T')[0];
        },
        description: 'Honor American workers',
        type: 'federal',
    },
    {
        id: 'columbus-day',
        name: 'Columbus Day',
        calculator: (year) => {
            // 2nd Monday in October
            const date = getNthWeekdayOfMonth(year, 10, 1, 2);
            return date.toISOString().split('T')[0];
        },
        description: 'Columbus Day / Indigenous Peoples Day',
        type: 'federal',
    },
    {
        id: 'thanksgiving',
        name: 'Thanksgiving',
        calculator: (year) => {
            // 4th Thursday in November
            const date = getNthWeekdayOfMonth(year, 11, 4, 4);
            return date.toISOString().split('T')[0];
        },
        description: 'Thanksgiving Day',
        type: 'federal',
    },
];
/**
 * Generate all holiday dates for a given year range
 * @param startYear Start year
 * @param endYear End year
 * @returns Array of holidays with specific dates
 */
export function generateHolidaysForRange(startYear, endYear) {
    const holidays = [];
    for (let year = startYear; year <= endYear; year++) {
        // Fixed date holidays
        for (const holiday of US_HOLIDAYS) {
            holidays.push({
                ...holiday,
                fullDate: `${year}-${holiday.date}`,
            });
        }
        // Floating holidays
        for (const holiday of FLOATING_HOLIDAYS) {
            const holidayEntry = {
                id: holiday.id,
                name: holiday.name,
                date: '', // Not applicable for floating
                type: holiday.type,
                fullDate: holiday.calculator(year),
            };
            if (holiday.description) {
                holidayEntry.description = holiday.description;
            }
            holidays.push(holidayEntry);
        }
    }
    return holidays.sort((a, b) => a.fullDate.localeCompare(b.fullDate));
}
/**
 * Get all holidays for a specific schedule's date range
 * @param scheduleStartDate Schedule start date (YYYY-MM-DD)
 * @param scheduleEndDate Schedule end date (YYYY-MM-DD)
 */
export function getHolidaysForSchedule(scheduleStartDate, scheduleEndDate) {
    const startYear = parseInt(scheduleStartDate.split('-')[0], 10);
    const endYear = parseInt(scheduleEndDate.split('-')[0], 10);
    const allHolidays = generateHolidaysForRange(startYear, endYear);
    // Filter to only holidays within schedule range
    return allHolidays.filter((holiday) => holiday.fullDate >= scheduleStartDate && holiday.fullDate <= scheduleEndDate);
}
